/* eslint-disable react-refresh/only-export-components -- co-locating Provider + hook is intentional */
import {
  createContext,
  useContext,
  useEffect,
  useId,
  type ReactNode,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  Block,
  NumberingStyle,
  NumberingStylePosition,
  StyleProfile,
} from '@worldathletics/types';
import { computeNameMap, type NamingNode } from '@worldathletics/utilities';

import {
  ReferencesProvider,
  type RefMeta,
} from '@/context/references/references-context';
import { ReferenceMetaStoreProvider } from '@/context/references/reference-meta-store-context';
import { NamesProvider } from '@/context/names/names-context';
import { EditLinkProvider } from '@/context/edit-link/edit-link-context';
import type { RootState } from '@/context/store/store';
import {
  clearScopedDocumentNames,
  setScopedDocumentNames,
} from '@/context/store/slices/documentNamesSlice';
import { StyleProfileProvider } from '@/lib/style-profile';

export type DocumentPreviewContext = {
  tree: Block[];
  numberingStyle: NumberingStyle;
  numberingStylePosition?: NumberingStylePosition;
  styleProfile?: StyleProfile | null;
  referencesMap?: Map<string, RefMeta>;
};

type DocumentNumberingDefaults = {
  numberingStylePosition?: NumberingStylePosition;
};

const DocumentNumberingDefaultsContext =
  createContext<DocumentNumberingDefaults | null>(null);

export function useDocumentNumberingDefaults(): DocumentNumberingDefaults {
  const previewDefaults = useContext(DocumentNumberingDefaultsContext);
  const nodePosition = useSelector(
    (state: RootState) => state.documentNode?.data?.numberingStylePosition,
  );

  if (previewDefaults?.numberingStylePosition != null) {
    return previewDefaults;
  }
  if (nodePosition != null) {
    return { numberingStylePosition: nodePosition };
  }
  return previewDefaults ?? {};
}

type DocumentPreviewProvidersProps = {
  context: DocumentPreviewContext;
  children: ReactNode;
  onEditLink?: () => void;
};

export function DocumentPreviewProviders({
  context,
  children,
  onEditLink,
}: DocumentPreviewProvidersProps) {
  const dispatch = useDispatch();
  const referencesMap = context.referencesMap ?? new Map<string, RefMeta>();
  const styleProfile = context.styleProfile ?? null;
  const numberingDefaults: DocumentNumberingDefaults = {
    ...(context.numberingStylePosition != null
      ? { numberingStylePosition: context.numberingStylePosition }
      : {}),
  };
  // Per-instance scope so a preview's numbering is isolated from the editor's
  // (and from other previews) — clearing on unmount only drops this scope.
  const scope = useId();

  useEffect(() => {
    const nameMap = computeNameMap(
      context.tree as unknown as NamingNode[],
      context.numberingStyle,
    );
    const record: Record<string, string> = {};
    for (const [id, name] of nameMap) {
      record[id] = name;
    }
    dispatch(setScopedDocumentNames({ scope, names: record }));
    return () => {
      dispatch(clearScopedDocumentNames(scope));
    };
  }, [context.tree, context.numberingStyle, dispatch, scope]);

  return (
    <DocumentNumberingDefaultsContext.Provider value={numberingDefaults}>
      <ReferencesProvider value={referencesMap}>
        <ReferenceMetaStoreProvider value={referencesMap}>
          <EditLinkProvider value={onEditLink ?? (() => undefined)}>
            <NamesProvider scope={scope}>
              <StyleProfileProvider profile={styleProfile}>
                {children}
              </StyleProfileProvider>
            </NamesProvider>
          </EditLinkProvider>
        </ReferenceMetaStoreProvider>
      </ReferencesProvider>
    </DocumentNumberingDefaultsContext.Provider>
  );
}
